import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const PRESET_PERIODS = ['7d', '15d', '30d'];
export const DEFAULT_PERIOD = '30d';
export const CUSTOM_PERIOD = 'custom';

const PRESET_DAYS = { '7d': 7, '15d': 15, '30d': 30 };
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Data local → 'yyyy-MM-dd' (o backend interpreta no fuso do servidor).
const toIsoDate = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const isValidDate = (value) => Boolean(value) && DATE_PATTERN.test(value);

/**
 * Normaliza { period, start, end } vindos da URL: período desconhecido ou
 * personalizado incompleto/invertido volta para o padrão (30 dias).
 */
export const normalizePeriod = ({ period, start, end }) => {
  if (period === CUSTOM_PERIOD && isValidDate(start) && isValidDate(end) && start <= end) {
    return { period, start, end };
  }
  if (PRESET_DAYS[period]) return { period, start: null, end: null };
  return { period: DEFAULT_PERIOD, start: null, end: null };
};

/**
 * Período → query params da API. Presets mandam só `start_date` (o fim é
 * "agora" no backend); o personalizado manda as duas datas (fim inclusivo).
 */
export const toApiParams = ({ period, start, end }, today = new Date()) => {
  if (period === CUSTOM_PERIOD) return { start_date: start, end_date: end };
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - PRESET_DAYS[period]);
  return { start_date: toIsoDate(from) };
};

/**
 * Período das métricas guardado na URL (`?period=7d` ou
 * `?period=custom&start=2026-01-01&end=2026-01-31`): o link é compartilhável e
 * sobrevive a reload. Compartilhado pelas páginas do mobile e do dashboard.
 */
export default function useMetricsPeriod() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPeriod = searchParams.get('period');
  const rawStart = searchParams.get('start');
  const rawEnd = searchParams.get('end');

  const value = useMemo(
    () => normalizePeriod({ period: rawPeriod, start: rawStart, end: rawEnd }),
    [rawPeriod, rawStart, rawEnd],
  );
  // Recalculado só quando o período muda: a data de hoje fica estável na queryKey.
  const params = useMemo(() => toApiParams(value), [value]);

  const setPeriod = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          const normalized = normalizePeriod(next);
          updated.delete('start');
          updated.delete('end');
          if (normalized.period === DEFAULT_PERIOD) {
            updated.delete('period');
          } else {
            updated.set('period', normalized.period);
          }
          if (normalized.period === CUSTOM_PERIOD) {
            updated.set('start', normalized.start);
            updated.set('end', normalized.end);
          }
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { value, params, setPeriod, isDefault: value.period === DEFAULT_PERIOD };
}
